import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import axiosInstance from '../../api/axiosConfig';
import './FormStyles.css';
import receptionBg from '../../assets/images/backgrounds/reception-bg.png';

const ContractorForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const isPublic = location.pathname.startsWith('/visitor/form');

    const [formData, setFormData] = useState({
        company: '',
        visitorName: '',
        email: '',
        contactNumber: '',
        serviceType: '',
        workArea: '',
        estimatedDuration: '',
        equipmentBrought: '',
        purpose: '',
        notes: ''
    });

    const API_URL = process.env.REACT_APP_API_URL || '';

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (isPublic) {
                await axios.post(
                    `${API_URL}/api/public/visitor-forms`,
                    {
                        formType: 'contractor',
                        formData,
                        notes: formData.notes
                    }
                );
            } else {
                await axiosInstance.post(
                    '/api/forms',
                    {
                        formType: 'contractor',
                        formData,
                        notes: formData.notes
                    }
                );
            }

            setSuccess(true);
            setTimeout(() => {
                navigate(isPublic ? '/visitor' : '/purpose-selection');
            }, 2000);
        } catch (error) {
            alert('Error submitting form: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigate(isPublic ? '/visitor' : '/purpose-selection');
    };

    return (
        <div className="form-container" style={{ backgroundImage: `url(${receptionBg})` }}>
            <div className="form-wrapper card fade-in">
                <div className="form-header">
                    <img src="/logo.png" alt="Logo" className="app-logo" />
                    <div className="form-header-text">
                        <h2>Contractor/Vendor Registration</h2>
                        <p>Service provider check-in</p>
                    </div>
                    <div className="form-header-actions">
                        {!isPublic && user && (
                            <div className="form-user-info">
                                <strong>{user?.fullName || user?.username}</strong>
                            </div>
                        )}
                        <button
                            type="button"
                            className="btn-back-form"
                            onClick={handleBack}
                        >
                            ← Back
                        </button>
                    </div>
                </div>

                {success && (
                    <div className="alert alert-success">
                        ✅ Contractor registered successfully! Redirecting...
                    </div>
                )}

                <form onSubmit={handleSubmit} className="visitor-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="companyName" className="form-label">Company Name *</label>
                            <input
                                type="text"
                                id="companyName"
                                name="companyName"
                                className="form-input"
                                value={formData.companyName}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="contactPerson" className="form-label">Contact Person *</label>
                            <input
                                type="text"
                                id="contactPerson"
                                name="contactPerson"
                                className="form-input"
                                value={formData.contactPerson}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="email" className="form-label">Email Address</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                className="form-input"
                                value={formData.email}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="phone" className="form-label">Phone Number *</label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                className="form-input"
                                value={formData.phone}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="serviceType" className="form-label">Service Type *</label>
                            <select
                                id="serviceType"
                                name="serviceType"
                                className="form-select"
                                value={formData.serviceType}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select service...</option>
                                <option value="maintenance">Maintenance</option>
                                <option value="cleaning">Cleaning</option>
                                <option value="it-support">IT Support</option>
                                <option value="construction">Construction</option>
                                <option value="electrical">Electrical</option>
                                <option value="plumbing">Plumbing</option>
                                <option value="security">Security</option>
                                <option value="other">Other</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label htmlFor="workArea" className="form-label">Work Area/Location *</label>
                            <input
                                type="text"
                                id="workArea"
                                name="workArea"
                                className="form-input"
                                value={formData.workArea}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="estimatedDuration" className="form-label">Estimated Duration</label>
                            <select
                                id="estimatedDuration"
                                name="estimatedDuration"
                                className="form-select"
                                value={formData.estimatedDuration}
                                onChange={handleChange}
                            >
                                <option value="">Select duration...</option>
                                <option value="1hour">1 hour</option>
                                <option value="2-4hours">2-4 hours</option>
                                <option value="halfday">Half day</option>
                                <option value="fullday">Full day</option>
                                <option value="multiday">Multiple days</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label htmlFor="equipmentBrought" className="form-label">Equipment Brought</label>
                            <input
                                type="text"
                                id="equipmentBrought"
                                name="equipmentBrought"
                                className="form-input"
                                value={formData.equipmentBrought}
                                onChange={handleChange}
                                placeholder="e.g., Tools, Ladder, etc."
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="notes" className="form-label">Additional Notes</label>
                        <textarea
                            id="notes"
                            name="notes"
                            className="form-textarea"
                            value={formData.notes}
                            onChange={handleChange}
                            rows="3"
                        ></textarea>
                    </div>

                    <div className="form-actions">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleBack}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                        >
                            {loading ? 'Submitting...' : 'Register Contractor'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ContractorForm;
